import { t } from 'i18next';
import { isNil } from 'lodash';
import { Controller, Form, FormProvider } from 'react-hook-form';
import { Box, Button, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import {
  CustomModal,
  FormGooglePlacesAutocomplete,
  SaveChangesModal,
} from '@components';
import { theme } from '@store';

import { useCreateOperator } from './hooks';
import { USStateSelect } from 'components/USStateSelect';
import { Select } from 'ui';

export const CreateOperator = ({
  handleOpenOperatorDetails,
}: {
  handleOpenOperatorDetails: (id: string) => void;
}) => {
  const {
    formMethods,
    handleClose,
    handleOnClick,
    isOpen,
    isSubmitting,
    onSubmit,
    partnerOptions,
    organizationOptions,
    isPartnerSelectDisabled,
    handleSelectLocation,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleDiscardChanges,
    handleSaveChanges,
    isValid,
    isEditSubmitting,
  } = useCreateOperator(handleOpenOperatorDetails);

  const { control } = formMethods;

  return (
    <>
      <Button
        startIcon={<AddIcon />}
        sx={{ fontWeight: 'regular', fontSize: '1rem', color: 'white' }}
        color='secondary'
        variant='contained'
        onClick={handleOnClick}
      >
        {t('adminPanel.operators.addModal.addOperator')}
      </Button>
      <CustomModal
        isOpen={isOpen}
        onClose={handleClose}
        title={t('adminPanel.operators.addModal.title')}
        isLoading={isSubmitting}
      >
        <FormProvider {...formMethods}>
          <Form
            control={control}
            onSubmit={e => onSubmit(e.event)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            <Controller
              name='name'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  type='text'
                  label={t('adminPanel.operators.addModal.name')}
                  variant='outlined'
                  error={!isNil(error)}
                  helperText={error ? error.message : null}
                  required
                  fullWidth
                />
              )}
            />

            {!!organizationOptions.length && (
              <Controller
                name='organization_id'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Select
                    {...field}
                    label={t('adminPanel.operators.addModal.organization')}
                    error={!isNil(error)}
                    required
                    helperText={error?.message}
                    options={organizationOptions}
                  />
                )}
              />
            )}

            <Controller
              name='partner_id'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <Select
                  {...field}
                  type='text'
                  label={t('adminPanel.operators.addModal.partner')}
                  variant='outlined'
                  error={!isNil(error)}
                  required
                  fullWidth
                  disabled={isPartnerSelectDisabled}
                  options={partnerOptions}
                  helperText={error?.message}
                />
              )}
            />

            <Typography
              variant='subtitle2'
              sx={{
                width: '100%',
                textAlign: 'left',
              }}
            >
              {t('adminPanel.operators.addModal.contactInformation')}
            </Typography>

            <FormGooglePlacesAutocomplete onChange={handleSelectLocation} />

            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                flexDirection: 'row',
                gap: '0.5rem',
              }}
            >
              <Controller
                name='street'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    type='text'
                    label={t('adminPanel.operators.addModal.addressLine1')}
                    variant='outlined'
                    error={!isNil(error)}
                    helperText={error ? error.message : null}
                    required
                    fullWidth
                  />
                )}
              />
              <Controller
                name='suite'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    type='text'
                    label={t('adminPanel.operators.addModal.addressLine2')}
                    variant='outlined'
                    error={!isNil(error)}
                    helperText={error ? error.message : null}
                    fullWidth
                  />
                )}
              />
            </Box>

            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                flexDirection: 'row',
                gap: '0.5rem',
              }}
            >
              <Controller
                name='city'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    type='text'
                    label={t('adminPanel.operators.addModal.city')}
                    variant='outlined'
                    error={!isNil(error)}
                    helperText={error ? error.message : null}
                    required
                    fullWidth
                  />
                )}
              />
              <Controller
                name='state'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <USStateSelect
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ''}
                    required
                  />
                )}
              />
            </Box>

            <Controller
              name='zip_code'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  type='text'
                  label={t('adminPanel.operators.addModal.zip')}
                  variant='outlined'
                  error={!isNil(error)}
                  helperText={error ? error.message : null}
                  required
                  fullWidth
                />
              )}
            />

            <Controller
              name='county'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  type='text'
                  label={t('adminPanel.operators.addModal.county')}
                  variant='outlined'
                  error={!isNil(error)}
                  helperText={error ? error.message : null}
                  fullWidth
                />
              )}
            />

            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '8px',
                position: 'sticky',
                bottom: 0,
                paddingY: 3,
                backgroundColor: theme.palette.common.white,
                zIndex: 3,
              }}
            >
              <Button
                variant='text'
                sx={{
                  fontSize: '0,875rem',
                  fontWeight: 'medium',
                  lineHeight: '24px',
                }}
                disabled={isSubmitting}
                onClick={handleClose}
              >
                {t('general.cancel')}
              </Button>
              <Button
                type='submit'
                variant='contained'
                color='secondary'
                sx={{
                  fontSize: '0,875rem',
                  fontWeight: 'medium',
                  lineHeight: '24px',
                  color: 'white',
                }}
                disabled={isSubmitting}
              >
                {t('general.ADD')}
              </Button>
            </Box>
          </Form>
        </FormProvider>
      </CustomModal>
      <SaveChangesModal
        isOpen={isSaveChangesModalOpen}
        onClose={handleCloseSaveChangesModal}
        isValid={isValid}
        handleSaveChanges={handleSaveChanges}
        discardChanges={handleDiscardChanges}
        isLoading={isEditSubmitting}
      />
    </>
  );
};
